import { useCallback, useMemo, useRef } from 'react';

import { useFormController } from '@dreryk/react-common';
import {
   Modal,
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   ModalMaxHeight,
   ModalWidth,
} from '@dreryk/react-components';

import { appointmentsTranslationKeys } from '../../translations';

import { MedicalProfileSaveAction } from './actions/MedicalProfileSaveAction';
import { DeepLinkTarget } from './header/SectionSummary.types';
import { MedicalProfileFormContext, MedicalProfileFormContextValue } from './MedicalProfileFormContext';
import { useStableRegister } from './MedicalProfileModal.hooks';
import { SectionSavePayload } from './MedicalProfileModal.types';
import { MedicalProfileModalTabs } from './MedicalProfileModalTabs';
import { collectChangedSectionsFromForm, medicalProfileModalConfirmExitConfig } from './MedicalProfileModal.utils';
import { MedicalProfileTabFormBean, MedicalProfileTabViewModel } from './tab/MedicalProfileTab.types';

type MedicalProfileModalProps = {
   readonly patientId: number;
   readonly readonly: boolean;
   readonly initialTarget?: DeepLinkTarget | null;
   readonly onClose: VoidFunction;
};

export const MedicalProfileModal = (props: MedicalProfileModalProps): JSX.Element => {
   const { register, control, watch, getValues } = useFormController<MedicalProfileTabFormBean>(
      {},
      { shouldUnregister: false }
   );
   const stableRegister = useStableRegister(register);
   const viewModelsRef = useRef(new Map<string, MedicalProfileTabViewModel>());
   const formValues = watch();

   const registerViewModel = useCallback((viewModel: MedicalProfileTabViewModel): void => {
      viewModelsRef.current.set(viewModel.templateCode, viewModel);
   }, []);

   const contextValue = useMemo<MedicalProfileFormContextValue>(
      () => ({
         register: stableRegister,
         control: control,
         readonly: props.readonly,
         registerViewModel: registerViewModel,
      }),
      [stableRegister, control, props.readonly, registerViewModel]
   );

   const collectPayloads = (): SectionSavePayload[] =>
      collectChangedSectionsFromForm(viewModelsRef.current, getValues());

   const isDirty = collectChangedSectionsFromForm(viewModelsRef.current, formValues).length > 0;

   return (
      <Modal
         testId={'medicalProfile'}
         title={appointmentsTranslationKeys.medicalProfile.title}
         width={ModalWidth.HUGE}
         height={ModalMaxHeight.TALL}
      >
         <MedicalProfileFormContext.Provider value={contextValue}>
            <MedicalProfileModalTabs patientId={props.patientId} initialTarget={props.initialTarget} />
         </MedicalProfileFormContext.Provider>
         {props.readonly ? null : (
            <MedicalProfileSaveAction onSuccess={props.onClose}>
               {({ saveSections }) => {
                  const handleSave = async (): Promise<void> => {
                     const payloads = collectPayloads();
                     // TODO DREGAB-29796: temporary debug output, remove together with the payload wiring
                     /* eslint-disable no-console */
                     console.log('[medicalProfile] payloads', payloads);
                     console.log('[medicalProfile] raw form values', getValues());
                     /* eslint-enable no-console */
                     return saveSections(payloads);
                  };

                  return (
                     <ModalConfirmButton
                        testId={'save'}
                        label={appointmentsTranslationKeys.medicalProfile.actions.save}
                        onClick={handleSave}
                        blockingOperation
                     />
                  );
               }}
            </MedicalProfileSaveAction>
         )}
         <ModalCancelButton
            label={appointmentsTranslationKeys.medicalProfile.actions.close}
            onClick={props.onClose}
            shouldConfirmExit={medicalProfileModalConfirmExitConfig(isDirty)}
         />
         <ModalCloseButton onClick={props.onClose} shouldConfirmExit={medicalProfileModalConfirmExitConfig(isDirty)} />
      </Modal>
   );
};
